/**
 * `useBulkAssignApprovalChain` mutation hook.
 *
 * Wraps the raw `bulkAssignApprovalChain` service fn (per AD-23). Invalidates the
 * folder files query (many assets change at once) and the project's approval chain
 * queries (`assetUsageCount` moves). Also invalidates the whole asset-approval-chain
 * namespace so any open asset detail popover refreshes, every file detail (overriding an
 * approved asset's chain reopens it), and the folder list/detail aggregates so the
 * progress bar recomputes.
 *
 * The `{ assigned, skipped, total }` result is returned to the caller so the
 * dialog can report counts in its success toast. Caller owns error UX.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';
import { APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/approval-chain';
import { track } from '@/shared/analytics';

import { bulkAssignApprovalChain } from './asset-approval-chain.mutations.api';
import { invalidateFolderAggregates } from './invalidate-folder-aggregates';

export { bulkAssignApprovalChain };

interface UseBulkAssignApprovalChainOptions {
  projectId: string;
  /** Folder currently in view — used to refresh its files list after assigning. */
  folderId?: string;
}

interface BulkAssignApprovalChainVars {
  approvalChainId: string;
  assetIds: string[];
  overrideExisting: boolean;
}

export function useBulkAssignApprovalChain(options: UseBulkAssignApprovalChainOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: ({ approvalChainId, assetIds, overrideExisting }: BulkAssignApprovalChainVars) =>
      bulkAssignApprovalChain(projectId, approvalChainId, assetIds, overrideExisting),
    onSuccess: (result, { overrideExisting }) => {
      void queryClient.invalidateQueries({
        queryKey: APPROVAL_CHAIN_QUERY_KEYS.all(projectId),
      });
      // Broad prefix invalidation — refresh any open asset-approval-chain query.
      void queryClient.invalidateQueries({
        queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.namespace(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_QUERY_KEYS.details(projectId),
      });
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FOLDER_QUERY_KEYS.files(projectId, folderId),
        });
      }
      invalidateFolderAggregates(queryClient, projectId, folderId);
      track('assets_workflow_bulk_assigned', {
        project_id: projectId,
        override_existing: overrideExisting,
        assigned_count: result.assigned,
        skipped_count: result.skipped,
        total_count: result.total,
      });
    },
  });
}
